import {
  WORKBOOK_MAX_COLUMNS,
  workbookColumnSchema,
  type WorkbookColumnarSheet,
} from '@erp/shared-types';
import { columnReference, type ColumnType } from '@erp/workbooks';

type Column = WorkbookColumnarSheet['columns'][number];

/** What Add column does with the picked Fields and Formula. */
export type AddColumnsPlan =
  | { kind: 'add'; columns: Column[] }
  /** A clashing or blank name, or a Formula that doesn't parse. */
  | { kind: 'invalid' }
  /** Nothing to add, or more than a Worksheet holds. */
  | { kind: 'none' };

export interface AddColumnsInput {
  /** The Worksheet's columns now. */
  existing: readonly Column[];
  /** The picked Fields not yet columns, in pick order. */
  additions: readonly string[];
  /** The name and type the form shows: a single Field's, or a Formula column's. */
  name: string;
  type: Column['type'];
  formula: string;
  /** Suggested types for columns made from Fields. */
  defaultTypes: ReadonlyMap<string, ColumnType>;
}

const key = (name: string) => name.trim().toLowerCase();

/**
 * The columns Add column appends: a column per picked Field, and a Formula
 * column when a Formula is typed. With a single Field and no Formula, the
 * form's name and type apply to it; a Formula column named like the single
 * Field replaces it.
 */
export function planAddedColumns(input: AddColumnsInput): AddColumnsPlan {
  const { additions, formula } = input;
  const single = additions.length === 1 ? additions[0] : undefined;
  const custom = !!single && !formula.trim();
  const columns: Column[] = additions.map((column) => ({
    name: custom ? input.name : column,
    expression: columnReference(column),
    type: custom ? input.type : (input.defaultTypes.get(column) ?? 'text'),
  }));
  if (formula.trim()) {
    const added = formulaColumn(input, single);
    if (!added) return { kind: 'invalid' };
    if (single && key(input.name) === single.toLowerCase())
      columns.splice(0, 1, added);
    else columns.push(added);
  }
  return checkedPlan(input.existing, columns);
}

/** The typed Formula as a column, or null if it doesn't parse or its name clashes. */
function formulaColumn(
  input: AddColumnsInput,
  single: string | undefined,
): Column | null {
  const parsed = workbookColumnSchema.safeParse({
    name: input.name,
    expression: input.formula,
    type: input.type,
  });
  if (!parsed.success) return null;
  const name = key(input.name);
  const existing = input.existing.some(
    (column) => column.name.toLowerCase() === name,
  );
  const clashesWithField =
    !single && input.additions.some((column) => column.toLowerCase() === name);
  return existing || clashesWithField ? null : parsed.data;
}

function checkedPlan(
  existing: readonly Column[],
  columns: Column[],
): AddColumnsPlan {
  if (
    !columns.length ||
    existing.length + columns.length > WORKBOOK_MAX_COLUMNS
  )
    return { kind: 'none' };
  const all = [...existing, ...columns];
  const unique = new Set(all.map((column) => key(column.name))).size;
  if (unique !== all.length || columns.some((column) => !column.name.trim()))
    return { kind: 'invalid' };
  return { kind: 'add', columns };
}
